<template>
  <div class="mx-4 mt-3 flex flex-grow flex-col md:mx-8 md:mt-7 md:flex-row">
    <div class="mx-auto mb-3 md:hidden">
      <img
        v-if="applicationSetup.hotel_logo_mobile"
        fetchpriority="high"
        decoding="async"
        class="h-24 max-h-24 max-w-full object-contain"
        :src="applicationSetup.hotel_logo_mobile"
        :alt="applicationSetup.hotel_name" />
      <span v-else>{{ applicationSetup.hotel_name }}</span>
    </div>

    <Calendar
      v-if="!isMobile"
      class="hidden md:relative md:block md:basis-3/4 lg:pr-8"
      :class="visibleTooltip"
      :initialStartDate="query.startDate"
      :initialEndDate="query.endDate"
      :maxDate="applicationSetup.end_date"
      :showBar="applicationSetup.show_bar"
      :currency="applicationSetup.currency"
      :currencySymbol="applicationSetup.currencySymbol"
      :focusOn="focusOn"
      @datesSelected="datesSelected"
      @setDatesSelected="setDatesSelected" />

    <div class="flex flex-grow flex-col md:ml-7 md:basis-1/3">
      <div class="mb-3 flex flex-row">
        <DateCard
          :title="$t('general.checkIn')"
          :date="format(query.startDate, 'yyyy-MM-dd')"
          @click="calendarVisibleToggle('startDate')"
          cardClass="mr-2" />
        <DateCard
          :title="$t('general.checkOut')"
          :date="format(query.endDate, 'yyyy-MM-dd')"
          @click="calendarVisibleToggle('endDate')" />
      </div>

      <QuerySelectButton
        v-for="(button, index) in querySelectButtonOption"
        :key="index"
        :title="button.title"
        :body="button.body"
        :component="button.component"
        @click="button.onClick"
        :aria-label="button.ariaLabel" />
      <Button
        :class="'primary sticky bottom-4'"
        :height="'h-[60px]'"
        :width="'w-full'"
        :label="$t('general.viewRates')"
        @click="viewRates"
        spanClass="tracking-wide" />
    </div>
    <CustomDrawer
      v-model="drawerOpen"
      :position="isMobile ? 'bottom' : 'right'"
      width="w-[35%]"
      height="h-auto"
      :withBlur="currentComponentName === 'Playground' ? false : true"
      @update:modelValue="onDrawerOpenClose">
      <template #drawerContent>
        <component
          :is="currentComponent"
          @close="drawerOpen = false"></component>
      </template>
    </CustomDrawer>

    <CustomDrawer
      v-model="calendarOpen"
      position="bottom"
      width="w-[35%]"
      height="h-auto"
      v-if="isMobile">
      <template #drawerContent>
        <Calendar
          :initialStartDate="query.startDate"
          :initialEndDate="query.endDate"
          :maxDate="applicationSetup.end_date"
          :showBar="applicationSetup.show_bar"
          :currencySymbol="applicationSetup.currencySymbol"
          @datesSelected="datesSelected"
          @setDatesSelected="setDatesSelected"
          @cancel="calendarVisibleToggle"
          @close="toogleCalendar" />
      </template>
    </CustomDrawer>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  shallowRef,
  watch,
  inject,
  defineAsyncComponent,
} from 'vue';
import { storeToRefs } from 'pinia';
import { format, addDays } from 'date-fns';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useScreenSize } from '@composables/useScreenSize';
import { useOffersStore } from '@stores/offers.store';

import Calendar from '@/components/CalendarCore.vue';
const CustomDrawer = defineAsyncComponent(
  () => import('@/components/customComponents/CustomDrawer.vue'),
);
const Button = defineAsyncComponent(
  () => import('@/components/customComponents/CustomButton.vue'),
);
const DateCard = defineAsyncComponent(
  () => import('@/components/mobile/DateCard.vue'),
);
const OccupancyRoom = defineAsyncComponent(
  () => import('@/components/Occupancy/OccupancyRoom.vue'),
);
const QuerySelectButton = defineAsyncComponent(
  () => import('@/components/QuerySelectButton.vue'),
);
const SpecialRates = defineAsyncComponent(
  () => import('@/components/SpecialRates/SpecialRates.vue'),
);
const DirectIncentive = defineAsyncComponent(
  () => import('@/components/directIncentive/DirectIncentive.vue'),
);
const PlaygroundView = defineAsyncComponent(
  () => import('./PlaygroundView.vue'),
);
const PromotionChipList = defineAsyncComponent(
  () => import('@/components/customComponents/PromotionChipList.vue'),
);

import { i18n } from '@/locales/i18n';
import router from '@/router';
import { useRoute } from 'vue-router';
import { useReservationStore } from '@/stores/reservation.store';

const reservationStore = useReservationStore();
const { resetReservation } = reservationStore;

const route = useRoute();

useOffersStore();
const { isMobile } = useScreenSize();
const env = import.meta.env.MODE;
const bookingQueryStore = useBookingQueryStore();
const applicationSetup = inject('applicationSetup');

const { setStartDate, setEndDate } = bookingQueryStore;
const { query, userOccupancyTotals } = storeToRefs(bookingQueryStore);

const drawerOpen = ref(false);
const currentComponent = shallowRef(null);
const currentComponentName = ref('');

const calendarOpen = ref(false);

const onDrawerOpenClose = () => {
  if (currentComponentName.value === 'Promos') {
    const invalidChip = document.querySelector('.invalid-chip');
    const chipInputElement = document.querySelector('.chip-input');
    const invalidChipValue = chipInputElement ? chipInputElement.value : '';
    if (invalidChip || invalidChipValue.length > 0) {
      drawerOpen.value = true;
    }
  }
};

const toogleCalendar = () => {
  calendarOpen.value = !calendarOpen.value;
};

const querySelectButtonOption = computed(() => [
  {
    title: i18n.global.t('general.occupancy.title'),
    body:
      i18n.global.t('general.roomsTitle', {
        n: userOccupancyTotals.value.totalRooms,
      }) +
      ', ' +
      i18n.global.t('general.occupancy.adults', {
        n: userOccupancyTotals.value.totalAdults,
      }) +
      (userOccupancyTotals.value.totalChildren > 0
        ? ' ' +
          i18n.global.t('general.occupancy.children', {
            n: userOccupancyTotals.value.totalChildren,
          })
        : '') +
      (userOccupancyTotals.value.totalBabies > 0
        ? ' ' +
          i18n.global.t('general.occupancy.babies', {
            n: userOccupancyTotals.value.totalBabies,
          })
        : ''),
    onClick: () => querySelectButtonAction('Occupancy', OccupancyRoom),
    ariaLabel: 'Occupancy',
  },
  {
    title: i18n.global.t('promos'),
    component: PromotionChipList,
    onClick: () => querySelectButtonAction('Promos', SpecialRates),
    ariaLabel: i18n.global.t('promosAndSpecialRates'),
  },

  ...(env.toLowerCase() === 'development'
    ? [
        {
          title: i18n.global.t('directIncentive'),
          body: i18n.global.t('directIncentivesInfo'),
          onClick: () =>
            querySelectButtonAction('Direct incentive', DirectIncentive),
          ariaLabel: 'Direct incentive',
        },
        {
          title: 'Test template',
          body: 'Click and see the magic',
          onClick: () => querySelectButtonAction('Playground', PlaygroundView),
          ariaLabel: 'PlaygroundView',
        },
      ]
    : []),
]);

const querySelectButtonAction = (id, component) => {
  currentComponentName.value = id;
  drawerOpen.value = !drawerOpen.value;
  currentComponent.value = component;
};

const state = ref({
  datesSelected: {
    startDate: query.value.startDate,
    endDate: query.value.endDate,
  },
});

const viewRates = () => {
  let query = route.query;
  if (state.value.datesSelected.endDate === '') {
    state.value.datesSelected.endDate = addDays(
      state.value.datesSelected.startDate,
      1,
    );
    setDatesSelected();
    query = {
      ...route.query,
      startDay: format(state.value.datesSelected.startDate, 'yyyy-MM-dd'),
      nrNights: 1,
    };
  }
  router.push({ name: 'search', query: query });
};

const datesSelected = (dates) => {
  state.value.datesSelected.startDate = dates[0];
  state.value.datesSelected.endDate = dates[1];
};

const setDatesSelected = () => {
  calendarVisibleToggle();

  setStartDate(state.value.datesSelected.startDate);
  setEndDate(state.value.datesSelected.endDate);

  resetReservation();
};

const visibleTooltip = ref('');
const focusOn = ref(null);
const calendarVisibleToggle = (date) => {
  visibleTooltip.value =
    visibleTooltip.value == date + '-tooltip' ? '' : date + '-tooltip';
  if (isMobile.value === true) calendarOpen.value = !calendarOpen.value;

  focusOn.value = date;
};

watch(isMobile, (newValue) => {
  if (!newValue) calendarOpen.value = false;
});
</script>

<style scoped>
::v-deep(.dates-selection span) {
  @apply gc-font-title;
}
</style>
